import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Tooltip, Typography } from '@mui/material'
import TuneIcon from '@mui/icons-material/TuneRounded'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { colors } from '../../const/colors'

export type UserDetailsTab = 'settings' | 'reservations'

const Tab: FC<{
  icon: ReactNode
  label: string
  active: boolean
  locked?: boolean
  badge?: number
  e2e: string
  onClick: () => void
}> = ({ icon, label, active, locked, badge, e2e, onClick }) => (
  <Box
    onClick={() => !locked && onClick()}
    data-e2e={e2e}
    aria-disabled={!!locked}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      height: 48,
      mb: '-1px',
      borderBottom: `2.5px solid ${active ? colors.navy : 'transparent'}`,
      color: locked
        ? colors.slateLight
        : active
          ? colors.navy
          : colors.textMuted,
      cursor: locked ? 'not-allowed' : 'pointer',
      fontSize: '13.5px',
      fontWeight: 800,
      letterSpacing: '-0.01em',
      transition: 'color .15s, border-color .15s',
      '&:hover': locked || active ? undefined : { color: colors.navy },
    }}
  >
    {locked ? <LockOutlinedIcon sx={{ fontSize: 17 }} /> : icon}
    {label}
    {!!badge && (
      <Box
        component="span"
        sx={{
          minWidth: 20,
          height: 20,
          px: '6px',
          borderRadius: '10px',
          background: active ? colors.navy : colors.bg,
          color: active ? '#fff' : colors.textMuted,
          fontSize: '11.5px',
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {badge}
      </Box>
    )}
  </Box>
)

const UserDetailsTabs: FC<{
  value: UserDetailsTab
  onChange: (tab: UserDetailsTab) => void
  reservationsLocked: boolean
  reservationsCount: number
}> = ({ value, onChange, reservationsLocked, reservationsCount }) => {
  const { t } = useTranslation(['user_details'])

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '26px',
        p: '0 24px',
        background: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <Tab
        icon={<TuneIcon sx={{ fontSize: 17 }} />}
        label={t('tab_settings')}
        e2e="tab-settings"
        active={value === 'settings'}
        onClick={() => onChange('settings')}
      />
      <Tooltip
        title={reservationsLocked ? t('unlock_after_assign') : ''}
        placement="bottom"
      >
        <Box sx={{ display: 'flex' }}>
          <Tab
            icon={<CalendarMonthIcon sx={{ fontSize: 17 }} />}
            label={t('tab_reservations')}
            e2e="tab-reservations"
            active={value === 'reservations'}
            locked={reservationsLocked}
            badge={reservationsCount}
            onClick={() => onChange('reservations')}
          />
        </Box>
      </Tooltip>
      {reservationsLocked && (
        <Typography
          sx={{
            ml: 'auto',
            fontSize: '11.5px',
            color: colors.slate,
            fontWeight: 600,
          }}
        >
          {t('assign_to_unlock')}
        </Typography>
      )}
    </Box>
  )
}

export default UserDetailsTabs
